import { useState } from 'react';
import { Boxes, DollarSign, Barcode, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BulkStatusInventoryDialog } from './BulkStatusInventoryDialog';
import { BulkPricingDialog } from './BulkPricingDialog';
import { BulkVariantFieldsDialog } from './BulkVariantFieldsDialog';
import { BulkProductFieldsDialog } from './BulkProductFieldsDialog';
import type { BulkTargets } from './types';

interface BulkEditActionsProps {
  targets: BulkTargets;
  /** Called after any bulk edit succeeds — pages use it to clear the selection. */
  onApplied: () => void;
}

type DialogId = 'status' | 'pricing' | 'variant' | 'product';

const ACTIONS: { id: DialogId; label: string; icon: typeof Boxes }[] = [
  { id: 'status', label: 'Inventory & status', icon: Boxes },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
  { id: 'variant', label: 'SKU & variant fields', icon: Barcode },
  { id: 'product', label: 'Tags, type & vendor', icon: Tags },
];

/**
 * The four bulk-edit actions that live inside the floating action bar. Shared
 * by the Products and Variants lists — each page just supplies its own
 * {@link BulkTargets} (selected products vs. selected variants).
 */
export function BulkEditActions({ targets, onApplied }: BulkEditActionsProps) {
  const [open, setOpen] = useState<DialogId | null>(null);
  const close = () => setOpen(null);

  return (
    <>
      {ACTIONS.map(({ id, label, icon: Icon }) => (
        <Button
          key={id}
          size="sm"
          variant="outline"
          className="h-9 justify-center gap-1.5 text-xs sm:h-8"
          onClick={() => setOpen(id)}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </Button>
      ))}

      <BulkStatusInventoryDialog
        open={open === 'status'}
        onOpenChange={(o) => !o && close()}
        targets={targets}
        onApplied={onApplied}
      />
      <BulkPricingDialog
        open={open === 'pricing'}
        onOpenChange={(o) => !o && close()}
        targets={targets}
        onApplied={onApplied}
      />
      <BulkVariantFieldsDialog
        open={open === 'variant'}
        onOpenChange={(o) => !o && close()}
        targets={targets}
        onApplied={onApplied}
      />
      <BulkProductFieldsDialog
        open={open === 'product'}
        onOpenChange={(o) => !o && close()}
        targets={targets}
        onApplied={onApplied}
      />
    </>
  );
}
